import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, CheckCircle2, Clock, AlertTriangle,
  Package, Truck, Building2, Factory, TrendingUp, ShoppingCart,
  Fuel, FileText, Wrench, AlertOctagon, IndianRupee, Timer,
  Droplets, Layers, ChevronRight, ArrowRight,
} from 'lucide-react';
import AIInsightsCard from '../components/AIInsightsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer group' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-2.5 rounded-lg ${color} flex-shrink-0 ml-3`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
    </div>
    {onClick && (
      <div className="mt-3 flex items-center text-xs text-slate-400 group-hover:text-slate-600 transition-colors">
        <span>View details</span>
        <ChevronRight className="h-3 w-3 ml-0.5" />
      </div>
    )}
  </div>
);

// ── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, icon: Icon, actionLabel, onAction, iconColor = 'text-slate-700' }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <Icon className={`h-4.5 w-4.5 ${iconColor}`} />
      <h2 className="text-base font-bold text-slate-800">{title}</h2>
    </div>
    {actionLabel && (
      <button onClick={onAction} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
        {actionLabel} <ArrowRight className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

// ── Yesterday Summary Table ───────────────────────────────────────────────────
const YesterdaySummary = ({ summary }) => {
  if (!summary) return null;

  const hasData = summary.total_trips > 0;
  const isGreen = summary.is_within_target;
  const hasDiesel = summary.actual_diesel > 0;

  const statusColor = hasDiesel
    ? (isGreen ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800')
    : 'bg-slate-50 border-slate-200 text-slate-600';

  const dotColor = hasDiesel ? (isGreen ? 'bg-green-500' : 'bg-red-500') : 'bg-slate-400';

  const statusLabel = hasDiesel
    ? (isGreen ? 'Within Diesel Target' : 'Over Diesel Target')
    : 'No Diesel Data';

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid="yesterday-summary">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-slate-800 rounded-lg flex items-center justify-center">
            <Layers className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-slate-900 text-sm">Yesterday's Production Summary</p>
            <p className="text-xs text-slate-500">{summary.date}</p>
          </div>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${statusColor}`}>
          <span className={`h-2 w-2 rounded-full ${dotColor}`} />
          {statusLabel}
        </div>
      </div>

      {hasData ? (
        <>
          {/* Material Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Material</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Trips</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Volume (m³)</th>
                  <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Target Diesel (L)</th>
                </tr>
              </thead>
              <tbody>
                {summary.materials.map((row, i) => (
                  <tr key={row.material} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                    <td className="px-5 py-3 font-medium text-slate-800">{row.material}</td>
                    <td className="px-4 py-3 text-right text-slate-700 tabular-nums">{row.trips.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-slate-700 tabular-nums font-medium">{row.cum.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                    <td className="px-5 py-3 text-right text-slate-500 tabular-nums">
                      {row.target_diesel.toLocaleString()} L
                      <span className="ml-1 text-xs opacity-60">×{row.factor}</span>
                    </td>
                  </tr>
                ))}
                {/* Total row */}
                <tr className="border-t border-slate-200 bg-slate-50 font-bold">
                  <td className="px-5 py-3 text-slate-900">TOTAL</td>
                  <td className="px-4 py-3 text-right text-slate-900 tabular-nums">{summary.total_trips.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-slate-900 tabular-nums">{summary.total_cum.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                  <td className="px-5 py-3 text-right text-slate-600 tabular-nums">{summary.total_target_diesel.toLocaleString()} L</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Diesel Footer */}
          <div className={`flex flex-wrap items-center justify-between gap-4 px-5 py-3.5 border-t ${
            hasDiesel
              ? (isGreen ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200')
              : 'bg-slate-50 border-slate-200'
          }`}>
            <div className="flex items-center gap-2">
              <Droplets className={`h-4 w-4 ${hasDiesel ? (isGreen ? 'text-green-600' : 'text-red-500') : 'text-slate-400'}`} />
              <span className={`text-sm font-semibold ${hasDiesel ? (isGreen ? 'text-green-800' : 'text-red-700') : 'text-slate-600'}`}>
                Actual Diesel Consumed
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-xs text-slate-500">Actual</p>
                <p className={`font-bold tabular-nums ${hasDiesel ? (isGreen ? 'text-green-800' : 'text-red-700') : 'text-slate-500'}`}>
                  {summary.actual_diesel.toLocaleString()} L
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-500">Target</p>
                <p className="font-semibold text-slate-700 tabular-nums">{summary.total_target_diesel.toLocaleString()} L</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-500">Rate</p>
                <p className={`font-semibold tabular-nums ${hasDiesel ? (isGreen ? 'text-green-800' : 'text-red-700') : 'text-slate-500'}`}>
                  {summary.diesel_per_cum} L/m³
                </p>
              </div>
              {hasDiesel && (
                <div className="text-center">
                  <p className="text-xs text-slate-500">{isGreen ? 'Saved' : 'Over'}</p>
                  <p className={`font-bold tabular-nums ${isGreen ? 'text-green-700' : 'text-red-600'}`}>
                    {Math.abs(summary.actual_diesel - summary.total_target_diesel).toLocaleString()} L
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
          <Layers className="h-6 w-6" />
          <p className="text-sm">No production data recorded for yesterday</p>
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const HomePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [yesterdaySummary, setYesterdaySummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers: token() }),
          axios.get(`${API}/dashboard/yesterday-summary`, { headers: token() }),
        ]);
        setStats(statsRes.data);
        setYesterdaySummary(summaryRes.data);
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
      </div>
    );
  }

  return (
    <div className="space-y-7 pb-8">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>
        <p className="text-slate-500 mt-0.5 text-sm">Welcome to LSN Operations</p>
      </div>

      {/* 1 ── Yesterday's Summary ──────────────────────────────────────────── */}
      <YesterdaySummary summary={yesterdaySummary} />

      {/* AI Operations Briefing ──────────────────────────────────────────── */}
      <AIInsightsCard />

      {/* 2 ── Production ───────────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Production" icon={Factory} iconColor="text-blue-600"
          actionLabel="Open" onAction={() => navigate('/production')} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard title="Today's Trips" value={stats?.production?.daily_trips || 0}
            icon={Factory} color="bg-blue-600" onClick={() => navigate('/production')} />
          <StatCard title="Today's Volume" value={`${stats?.production?.daily_cum || 0} m³`}
            icon={TrendingUp} color="bg-blue-500" onClick={() => navigate('/production')} />
          <StatCard title="Monthly Trips" value={stats?.production?.monthly_trips || 0}
            icon={Factory} color="bg-indigo-600" onClick={() => navigate('/production')} />
          <StatCard title="Monthly Volume" value={`${stats?.production?.monthly_cum || 0} m³`}
            icon={TrendingUp} color="bg-indigo-500" onClick={() => navigate('/production')} />
        </div>
      </div>

      {/* 3 ── Diesel ───────────────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Diesel" icon={Fuel} iconColor="text-rose-500"
          actionLabel="Open" onAction={() => navigate('/diesel')} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard title="Today's Consumption" value={`${stats?.diesel?.today_litres || 0} L`}
            subtitle="Litres filled today"
            icon={Fuel} color="bg-rose-500" onClick={() => navigate('/diesel')} />
          <StatCard title="Monthly Consumption" value={`${stats?.diesel?.month_litres || 0} L`}
            subtitle="Litres filled this month"
            icon={TrendingUp} color="bg-pink-500" onClick={() => navigate('/diesel')} />
        </div>
      </div>

      {/* 4 ── Maintenance ──────────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Maintenance" icon={Wrench} iconColor="text-amber-500"
          actionLabel="Open" onAction={() => navigate('/maintenance')} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard title="Open Tickets" value={stats?.maintenance?.open_tickets || 0}
            subtitle="Active maintenance logs"
            icon={Wrench} color={stats?.maintenance?.open_tickets > 0 ? "bg-amber-500" : "bg-green-500"}
            onClick={() => navigate('/maintenance')} />
          <StatCard title="Running Repair" value={stats?.maintenance?.running_repair || 0}
            subtitle="Minor repairs in progress"
            icon={Timer} color="bg-blue-500" onClick={() => navigate('/maintenance')} />
          <StatCard title="Breakdown" value={stats?.maintenance?.breakdown || 0}
            subtitle="Vehicles under breakdown"
            icon={AlertTriangle} color={stats?.maintenance?.breakdown > 0 ? "bg-red-500" : "bg-slate-400"}
            onClick={() => navigate('/maintenance')} />
          <StatCard title="Resolved Today" value={stats?.maintenance?.completed_today || 0}
            subtitle="Tickets closed today"
            icon={CheckCircle2} color="bg-green-600" onClick={() => navigate('/maintenance')} />
        </div>
      </div>

      {/* 5 ── Stores / Purchase Orders ────────────────────────────────────── */}
      <div>
        <SectionHeader title="Store & Inventory" icon={Package} iconColor="text-purple-600"
          actionLabel="Open" onAction={() => navigate('/inventory')} />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard title="Pending Indents" value={stats?.purchase_orders?.pending_indents || 0}
            subtitle="Awaiting approval"
            icon={ClipboardList} color="bg-yellow-500"
            onClick={() => navigate('/inventory/purchase-orders/indent')} />
          <StatCard title="Awaiting Quotation" value={stats?.purchase_orders?.awaiting_quotation || 0}
            subtitle="Approved, compare quotes"
            icon={FileText} color="bg-blue-500"
            onClick={() => navigate('/inventory/purchase-orders/quotations')} />
          <StatCard title="Pending Delivery" value={stats?.purchase_orders?.pending_delivery || 0}
            subtitle="Ordered, not received"
            icon={ShoppingCart} color="bg-orange-500"
            onClick={() => navigate('/inventory/purchase-orders/pending')} />
          <StatCard title="Overdue Deliveries" value={stats?.purchase_orders?.overdue_delivery || 0}
            subtitle="Ordered > 7 days, not received"
            icon={AlertOctagon}
            color={stats?.purchase_orders?.overdue_delivery > 0 ? "bg-red-600" : "bg-slate-400"}
            onClick={() => navigate('/inventory/purchase-orders/pending')} />
          <StatCard title="Completed This Month" value={stats?.purchase_orders?.completed_month || 0}
            subtitle="Received this month"
            icon={CheckCircle2} color="bg-green-500"
            onClick={() => navigate('/inventory/purchase-orders/completed')} />
          <StatCard title="Monthly Spend"
            value={stats?.purchase_orders?.month_value_inr > 0
              ? `₹${(stats.purchase_orders.month_value_inr / 1000).toFixed(0)}K` : '₹0'}
            subtitle="Total PO value this month"
            icon={IndianRupee} color="bg-teal-600"
            onClick={() => navigate('/inventory/purchase-orders/completed')} />
        </div>
        {/* Inventory SKU row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <StatCard title="Total SKUs" value={stats?.inventory?.total_skus || 0}
            icon={Package} color="bg-purple-500" onClick={() => navigate('/inventory')} />
          <StatCard title="Low Stock Items" value={stats?.inventory?.low_stock || 0}
            subtitle="Below minimum quantity"
            icon={AlertTriangle} color="bg-red-500" onClick={() => navigate('/inventory')} />
        </div>
      </div>

      {/* 6 ── Tasks ────────────────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Tasks" icon={ClipboardList} iconColor="text-slate-600"
          actionLabel="Open" onAction={() => navigate('/tasks')} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard title="Total Tasks" value={stats?.tasks?.total || 0}
            icon={ClipboardList} color="bg-slate-700" onClick={() => navigate('/tasks')} />
          <StatCard title="Open Tasks" value={stats?.tasks?.open || 0}
            subtitle="Pending & In Progress"
            icon={Clock} color="bg-blue-500" onClick={() => navigate('/tasks')} />
          <StatCard title="Completed" value={stats?.tasks?.completed || 0}
            icon={CheckCircle2} color="bg-green-500" onClick={() => navigate('/tasks')} />
          <StatCard title="Old Completed" value={stats?.tasks?.old_completed || 0}
            subtitle={`Over ${stats?.tasks?.days_threshold || 30} days`}
            icon={AlertTriangle} color="bg-amber-500" />
        </div>
      </div>

      {/* 7 ── Assets ───────────────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Assets" icon={Truck} iconColor="text-teal-600"
          actionLabel="Open" onAction={() => navigate('/assets')} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard title="Total Assets" value={stats?.assets?.total || 0}
            icon={Truck} color="bg-indigo-500" onClick={() => navigate('/assets')} />
          <StatCard title="Sites" value={stats?.assets?.sites || 0}
            icon={Building2} color="bg-teal-500" onClick={() => navigate('/assets')} />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
